/**
 * "Find more like this" exemplar-drawing state, shared between the Agent sidebar tab
 * (PromptView's label/chip UI) and MonitorProjectView's map (the
 * DrawLayer that captures rectangles, and the outline layer that renders them). The state
 * itself is owned by MonitorProjectView's useFewShotExemplars hook, mirroring how
 * OrderImageryState is defined here in components/ but held as page-level state — the map and the
 * sidebar are siblings, so neither owns the other.
 */

// Positive boxes are examples of what to find; negative boxes are look-alikes to ignore.
export type ExemplarPolarity = 'positive' | 'negative';

// Positives and negatives are separate lists, so a box is only identified by both fields.
export interface ExemplarRef {
  polarity: ExemplarPolarity;
  index: number;
}

export interface FewShotExemplarState {
  // Whether the map should be in rectangle-draw mode for a new exemplar.
  drawing: boolean;
  // Which list a finished rectangle lands in.
  drawingPolarity: ExemplarPolarity;
  // Names the notes the run creates; never sent to SAM.
  label: string;
  // Text sent to SAM with each list of boxes; blank matches on the boxes alone.
  searchTerm: string;
  negativeLabel: string;
  // Boxes drawn for the run currently being composed in PromptView.
  exemplars: GeoJSON.Polygon[];
  negativeExemplars: GeoJSON.Polygon[];
  // The box under the cursor, on either its map box or its sidebar chip.
  hovered: ExemplarRef | null;
  // The box open for moving/resizing on the map.
  editing: ExemplarRef | null;
}

export const DEFAULT_FEW_SHOT_EXEMPLAR_STATE: FewShotExemplarState = {
  drawing: false,
  drawingPolarity: 'positive',
  label: '',
  searchTerm: '',
  negativeLabel: '',
  exemplars: [],
  negativeExemplars: [],
  hovered: null,
  editing: null,
};

export function getExemplars(
  state: FewShotExemplarState,
  polarity: ExemplarPolarity
): GeoJSON.Polygon[] {
  return polarity === 'positive' ? state.exemplars : state.negativeExemplars;
}

// The backend refuses a run told to both find and skip the same word.
export function termsMatch({searchTerm, negativeLabel}: FewShotExemplarState): boolean {
  const term = searchTerm.trim().toLowerCase();
  return term !== '' && term === negativeLabel.trim().toLowerCase();
}

export function isSameExemplar(a: ExemplarRef | null, b: ExemplarRef | null): boolean {
  return a !== null && b !== null && a.polarity === b.polarity && a.index === b.index;
}

// The boxes a finished run was given, shown read-only on the map while RunView has it open.
export interface RunExemplars {
  positives: GeoJSON.Polygon[];
  negatives: GeoJSON.Polygon[];
}

export interface RunExemplarVisibility {
  positives: boolean;
  negatives: boolean;
}

// Why a drawn box would be refused at submit, mirroring common.lens.agent_runs.validate_few_shot.
export type ExemplarProblem =
  | 'outside'
  | 'too-small-imagery'
  | 'too-small-property'
  | 'too-big'
  | 'matches-example';

export interface FewShotExemplarFlow {
  fewShotState: FewShotExemplarState;
  // One entry per box in each list, null when the box passes.
  exemplarProblems: Record<ExemplarPolarity, (ExemplarProblem | null)[]>;
  setHoveredExemplar: (ref: ExemplarRef | null) => void;
  setFewShotLabel: (label: string) => void;
  setSearchTerm: (term: string) => void;
  setNegativeLabel: (label: string) => void;
  // Also switches which list the next box lands in, staying in draw mode.
  startDrawingExemplar: (polarity: ExemplarPolarity) => void;
  stopDrawingExemplar: () => void;
  addExemplar: (polygon: GeoJSON.Polygon) => void;
  updateExemplar: (ref: ExemplarRef, polygon: GeoJSON.Polygon) => void;
  removeExemplar: (ref: ExemplarRef) => void;
  // Opens a box for editing; `focusExemplar` also zooms the map to it.
  startEditingExemplar: (ref: ExemplarRef) => void;
  focusExemplar: (ref: ExemplarRef) => void;
  // With a ref, only closes that box; without one, closes whichever is open. resumeDrawing
  // goes back to draw mode, for when the user clicked off the box rather than leaving.
  stopEditingExemplar: (ref?: ExemplarRef, resumeDrawing?: boolean) => void;
  clearExemplars: () => void;
  resetFewShot: () => void;
}
